<template>
  <BfApplicationHeader
    :user="menuUser"
    :account-groups="accountGroups"
    :notifications="notifications"
    :logo-href="portalLink"
    logo-label="Bahnhof — start"
    navigation-id="app-sidebar"
    :navigation-open="navOpen"
    @update:navigation-open="$emit('toggle-nav')"
    @select="onSelect"
    @toggle="onToggle"
  >
    <template #logo>
      <img
        class="application-header__logo"
        :src="imageLogo"
        alt="Bahnhof Logo"
        width="115"
        height="60"
        fetchpriority="high"
      />
    </template>

    <template #center>
      <p v-if="isMasquerading" class="masquerade" role="status">
        <BaseIcon
          class="masquerade__icon"
          icon-name="icon-warning.svg"
          icon-color="orange"
          width="16px"
          height="16px"
          :is-without-hover="true"
        />
        <span class="masquerade__text">You are currently masquerading as a client.</span>
        <button
          type="button"
          class="masquerade__close"
          aria-label="Dismiss masquerading notice"
          data-test-id="notification-close"
          @click="dismissMasquerade"
        >
          <BaseIcon
            icon-name="cross-inline-icon.svg"
            icon-color="orange"
            width="12px"
            height="12px"
            :is-without-hover="true"
          />
        </button>
      </p>
    </template>
  </BfApplicationHeader>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

  import logo from '@/assets/img/logo.svg'
  import { getLocalStorageItem } from '@/utils'
  import { useNotificationStore } from '@/store/notification'
  import { useUserStore } from '@/modules/auth/store'
  import useHeaderNotifications from '@/composable/useHeaderNotifications'
  import BfApplicationHeader from '@/components/ui/bahnhof/BfApplicationHeader.vue'
  import BaseIcon from '@/components/ui/base/BaseIcon.vue'
  import BfIconBook from '@/components/ui/icons/BfIconBook.vue'
  import BfIconCard from '@/components/ui/icons/BfIconCard.vue'
  import BfIconEye from '@/components/ui/icons/BfIconEye.vue'
  import BfIconLock from '@/components/ui/icons/BfIconLock.vue'
  import BfIconMail from '@/components/ui/icons/BfIconMail.vue'
  import BfIconShield from '@/components/ui/icons/BfIconShield.vue'
  import BfIconSignOut from '@/components/ui/icons/BfIconSignOut.vue'
  import BfIconUser from '@/components/ui/icons/BfIconUser.vue'
  import type {
    BfApplicationMenuGroup,
    BfApplicationMenuItem,
    BfApplicationMenuUser,
  } from '@/typescript/types/ui-components/BfApplicationMenuI'

  const STORAGE_KEY = 'contrast-mode' as const

  withDefaults(defineProps<{ navOpen?: boolean }>(), { navOpen: false })
  defineEmits(['toggle-nav'])

  const authStore = useUserStore()
  const notificationStore = useNotificationStore()
  const { notifications } = useHeaderNotifications()

  const imageLogo = logo as string
  const portalLink = ref('')
  const isHighContrast = ref(false)

  function applyContrast(high: boolean): void {
    const root = document.documentElement
    high ? root.setAttribute('data-contrast', 'high') : root.removeAttribute('data-contrast')
  }

  function toggleContrast(): void {
    isHighContrast.value = !isHighContrast.value
    applyContrast(isHighContrast.value)
    try {
      localStorage.setItem(STORAGE_KEY, isHighContrast.value ? 'high' : 'normal')
    } catch {
      // A browser with storage blocked still gets the contrast, just not across reloads.
    }
  }

  function onStorage(e: StorageEvent): void {
    if (e.key === STORAGE_KEY && e.newValue) {
      const on = e.newValue === 'high'
      if (on !== isHighContrast.value) {
        isHighContrast.value = on
        applyContrast(on)
      }
    }
  }

  function dismissMasquerade(): void {
    notificationStore.dismissMasqueradeNotification()
  }

  function logout(): void {
    authStore.logoutUserWithToken()
  }

  const isMasquerading = computed<boolean>(() => {
    try {
      return (
        JSON.parse(getLocalStorageItem('isEnterAsAdmin') ?? 'false') &&
        !notificationStore.masqueradeNotificationDismissed
      )
    } catch {
      return false
    }
  })

  const fullName = computed<string>(() => {
    const user = authStore.getUser
    return user.fullname || [user.firstname, user.lastname].filter(Boolean).join(' ')
  })

  /** One or two letters, never a third — the design system's avatar is a mark, not a name. */
  const initials = computed<string>(() => {
    const user = authStore.getUser
    const letters = [user.firstname, user.lastname].filter(Boolean).map(part => String(part).charAt(0))
    return (letters.length ? letters : [fullName.value.charAt(0)]).join('').slice(0, 2).toUpperCase()
  })

  const menuUser = computed<BfApplicationMenuUser | null>(() =>
    fullName.value ? { name: fullName.value, email: authStore.getUser.email ?? '', initials: initials.value } : null,
  )

  /* The legacy list, unchanged in wording, order and destination — only its shape is the design
     system's. Sign out is its own group so it takes a divider, and `destructive` replaces the
     `main-dark-red` class the old menu matched on by name. */
  const accountGroups = computed<BfApplicationMenuGroup[]>(() => [
    {
      label: 'Account',
      items: [
        { label: 'Account details', to: '/account-details', icon: BfIconUser },
        // The design system's sprite has no multi-person glyph — only `icon-user`, which Account
        // details already carries — so Contacts takes the address book rather than a duplicate.
        { label: 'Contacts', to: '/contacts', icon: BfIconBook },
        { label: 'Payment methods', to: '/paymentmethods', icon: BfIconCard },
        { label: 'Email history', to: '/email-history', icon: BfIconMail },
      ],
    },
    {
      label: 'Security',
      items: [
        { label: 'Change password', to: '/change-password', icon: BfIconLock },
        { label: 'Security Settings', to: '/security-settings', icon: BfIconShield },
      ],
    },
    {
      label: 'Preferences',
      items: [{ label: 'High contrast', icon: BfIconEye, toggle: { modelValue: isHighContrast.value } }],
    },
    // The card's account panel draws exactly ONE divider, before the last, unlabelled group: the
    // labels above are what separate Account / Security / Preferences, so a hairline on each of
    // those would be the same boundary stated twice.

    { divided: true, items: [{ label: 'Sign out', destructive: true, icon: BfIconSignOut }] },
  ])

  // Rows with a `to` navigate themselves; only Sign out arrives here.
  function onSelect(item: BfApplicationMenuItem): void {
    if (item.label === 'Sign out') logout()
  }

  function onToggle(item: BfApplicationMenuItem): void {
    if (item.label === 'High contrast') toggleContrast()
  }

  onMounted(() => {
    portalLink.value = import.meta.env.VITE_PORTAL_API_URL

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'high') isHighContrast.value = true
      else if (stored === 'normal') isHighContrast.value = false
      else if (window.matchMedia?.('(prefers-contrast: more)').matches) isHighContrast.value = true
    } catch {
      // Fall through to the default (normal contrast).
    }

    applyContrast(isHighContrast.value)
    window.addEventListener('storage', onStorage)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('storage', onStorage)
  })
</script>

<style scoped lang="scss">
  /* The warning surface of BaseNotification at the scale of a header control: one line, no wrap,
     and it gives up its text before the bar gives up any of its own room. */
  .masquerade {
    display: flex;
    align-items: center;
    gap: var(--bf-sp-1);
    min-width: 0;
    max-width: 100%;
    margin: 0;
    padding: 6px var(--bf-sp-1) 6px var(--bf-sp-2);
    border-radius: var(--bf-r-pill);
    background-color: var(--bf-warning-soft);
    border: 1px solid var(--bf-warning-soft-border);
    color: var(--bf-warning-soft-text);
    font-size: var(--bf-fs-xs);
    line-height: 1.2;
  }

  .masquerade__icon {
    flex: 0 0 auto;
  }

  .masquerade__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .masquerade__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: var(--bf-r-pill);
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: background var(--bf-dur-fast) var(--bf-ease);

    &:hover {
      background: var(--bf-warning-soft-border);
    }

    &:focus-visible {
      outline: 2px solid var(--bf-orange-dark);
      outline-offset: 1px;
    }
  }

  /* Below --bf-bp-sm the bar is three 44px controls and its own padding; the sentence has nowhere
     to go, so the pill keeps only the warning mark and the dismiss control. */
  @media (max-width: 600px) {
    .masquerade {
      padding-left: var(--bf-sp-1);
    }

    .masquerade__text {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
  }
</style>
